import { useContext, useEffect, useMemo } from "react";

import { formatHumanName, ProfileResource } from "@medplum/core";
import { Task } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Checkbox, Drawer, Group, Stack } from "@mantine/core";
import { useForm, UseFormReturnType, zodResolver } from "@mantine/form";

import * as Sentry from "@sentry/react";

import {
  ChecklistTodosForm,
  checklistTodosFormInitialValues,
  ChecklistTodosFormType,
} from "@/clients/zod-schemas/checklist-todos";
import { TextWrapper } from "@/components/TextWrapper";
import { ChecklistTaskStatus } from "@/constants/checklist";
import { ProfileContext } from "@/contexts/profile";
import { updateChecklistTasks } from "@/services/checklist";
import { useAppDispatch } from "@/store";
import { searchAndUpdateChecklistTasks } from "@/store/slices/checklists";
import { longDateFormatWithTime } from "@/utils/datetime";
import { getCodeableConceptDisplay } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

type ChecklistTodosDrawerProps = {
  opened: boolean;
  onClose: () => void;
  tasks: Task[];
};

function ChecklistTodosDrawer({ opened, onClose, tasks }: ChecklistTodosDrawerProps) {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const { profile } = useContext(ProfileContext);

  const form = useForm<ChecklistTodosFormType>({
    initialValues: {
      selectedTasks: [],
    },
    validate: zodResolver(ChecklistTodosForm),
  });

  useEffect(() => {
    form.setValues({
      selectedTasks: checklistTodosFormInitialValues(tasks),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const onSubmit = async (values: ChecklistTodosFormType) => {
    const parsedValues = ChecklistTodosForm.parse(values);
    const selectedTasks = parsedValues.selectedTasks || [];

    if (!tasks.length) return;

    const tasksWithNewStatus = tasks.map((task) => ({
      ...task,
      status: selectedTasks.includes(task.id!)
        ? ChecklistTaskStatus.Completed
        : ChecklistTaskStatus.InProgress,
    }));

    try {
      const updatedTasks = await updateChecklistTasks(medplum, tasksWithNewStatus);

      if (updatedTasks.length) {
        dispatch(searchAndUpdateChecklistTasks(updatedTasks));
      }

      onClose();
    } catch (error) {
      // Keep the drawer open so the user can retry without losing their selection.
      showSystemErrorNotification();
      Sentry.captureException(error);
    }
  };

  return (
    <Drawer.Root
      size={512}
      opened={opened}
      position="right"
      onClose={onClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Drawer.Content>
          <Drawer.Header bg="gray.0">
            <Group align="center" justify="space-between">
              <Drawer.CloseButton />
              <Drawer.Title>To-Dos</Drawer.Title>
            </Group>
            <Button type="submit" disabled={!tasks.length} loading={form.submitting}>
              Save
            </Button>
          </Drawer.Header>
          <Drawer.Body my="md">
            <Stack>
              {tasks.map((task) => (
                <ChecklistTodosTask task={task} form={form} key={task.id} profile={profile} />
              ))}
            </Stack>
          </Drawer.Body>
        </Drawer.Content>
      </form>
    </Drawer.Root>
  );
}

function ChecklistTodosTask({
  task,
  form,
  profile,
}: {
  task: Task;
  form: UseFormReturnType<ChecklistTodosFormType>;
  profile?: ProfileResource;
}) {
  const isTaskCompleted = useMemo(
    () => task.status === ChecklistTaskStatus.Completed,
    [task.status],
  );
  const lastUpdatedBy = useMemo(() => task.meta?.author?.display, [task.meta?.author?.display]);
  const lastUpdated = useMemo(() => task.meta?.lastUpdated, [task.meta?.lastUpdated]);
  const taskTitle = useMemo(() => getCodeableConceptDisplay(task.code), [task.code]);
  const profileName = useMemo(() => formatHumanName(profile?.name?.[0]) || "", [profile]);
  const isChecked = useMemo(
    () => form.values.selectedTasks?.includes(task.id!),
    [form.values.selectedTasks, task.id],
  );

  const onCheckboxChange = () => {
    if (isChecked) {
      form.setFieldValue(
        "selectedTasks",
        form.values.selectedTasks?.filter((id) => id !== task.id!),
      );
      return;
    }

    form.setFieldValue("selectedTasks", [...(form.values.selectedTasks || []), task.id!]);
  };

  return (
    <Stack gap="xs">
      <Group gap={10} wrap="nowrap">
        <Checkbox
          {...form.getInputProps(`selectedTasks.${task.id}`, { type: "checkbox" })}
          color="green.7"
          value={task.id!}
          checked={isChecked}
          onChange={onCheckboxChange}
        />
        <TextWrapper ml="sm" fz={14} w="100%" c="dark.9">
          {taskTitle}
        </TextWrapper>
      </Group>
      {isTaskCompleted && isChecked && (
        <TextWrapper fz={12} fw={600} c="gray.6">
          Completed by {lastUpdatedBy} on {longDateFormatWithTime(lastUpdated)}
        </TextWrapper>
      )}
      {!isTaskCompleted && isChecked && (
        <TextWrapper fz={12} fw={600} c="gray.6">
          Completed by {profileName} on {longDateFormatWithTime(new Date().toISOString())}
        </TextWrapper>
      )}
    </Stack>
  );
}

export default ChecklistTodosDrawer;
